// Base styles first, so hero and section rules override them in the cascade.
import "./HomeRound5.css";
import "./blue2/base.css";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Play } from "lucide-react";
import { CLUB_FAQS, CLUB_INCLUDES, CLUB_SCHEDULE, JOIN_URL, PATREON_URL, THREAD_FRIEND_URL } from "@/lib/club";
import { SOCIALS } from "@/lib/social";
import { Draw, EASE, HEART, Head, Ink, VIEW, datePart, useFonts } from "./blue2/bits";
import { Nav } from "./blue2/Hero";
import { OnAir } from "./blue2/SectionsA";
import { Footer } from "./blue2/SectionsB";
import { Part, rootStyle } from "./blue2/parts";
import { LiveDates } from "./blue2/LiveDates";
import { EventStrip } from "./blue2/EventStrip";
import "./ClubBlue2.css";

/* The Loose Threads Club page matching blue-2 (the blue homepage in the team's
   "Team mix" palette). A copy of ClubBlue moved onto the --k-* role tokens;
   each section takes the palette its homepage twin uses. Copy follows the
   canonical /club-preview page. */

const CHECK = "M7 13 L11.5 17.5 L19.5 8";

/** A checkbox whose tick draws in, staggered by the parent list's "on" variant. */
function Tick({ i, base = 0.3 }: { i: number; base?: number }) {
  return <svg viewBox="0 0 26 26" aria-hidden="true"><rect x="2" y="2" width="22" height="22" rx="4" fill="none" stroke="currentColor" strokeWidth="2.4" />
    <motion.path d={CHECK} fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round"
      variants={{ off: { pathLength: 0, opacity: 0 }, on: { pathLength: 1, opacity: 1, transition: { delay: base + i * 0.2, duration: 0.45, opacity: { delay: base + i * 0.2, duration: 0.01 } } } }} /></svg>;
}

function Checks({ items, className }: { items: string[]; className: string }) {
  return <motion.ul className={`c2-checks ${className}`} initial="off" whileInView="on" viewport={VIEW}>
    {items.map((t, i) => <li key={t}><Tick i={i} /><span>{t}</span></li>)}
  </motion.ul>;
}

function Hero() {
  return <section className="b2-hero c2-hero" aria-labelledby="c2-title">
    <div className="c2-stage">
      <Nav club />
      <div className="c2-hero-grid">
        <div className="c2-hero-copy">
          <motion.p className="c2-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>Loose Threads Club · on Patreon</motion.p>
          <h1 id="c2-title" className="c2-title" aria-label="Loose Threads Club">
            <motion.span className="c2-title-hand" aria-hidden="true" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.5, duration: 0.5, ease: EASE }}>Loose Threads</motion.span>
            <span className="c2-title-word" aria-hidden="true">
              {"club".split("").map((c, i) => <motion.span key={i} initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
            </span>
          </h1>
          <div className="c2-script-row">
            <motion.p className="c2-script" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.45, ease: EASE }}>Your next project.<br />A little help.<br />A lot of fun.</motion.p>
            <svg className="c2-script-ticks" viewBox="0 0 42 62" aria-hidden="true">
              <Draw d="M6 18 L32 4" delay={1.2} width={4.5} /><Draw d="M10 34 L38 30" delay={1.28} width={4.5} /><Draw d="M8 50 L30 56" delay={1.36} width={4.5} />
            </svg>
          </div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.45, ease: EASE }}>
            <p className="c2-intro">Hi, I’m Chelsea! Pull up a chair and sew with me. I’ll help you make something you’re excited about, work through the tricky bits, and have some fun along the way.</p>
            <div className="b2-actions c2-actions">
              <span className="b2-youin-wrap">
                <a href={JOIN_URL} className="b2-btn b2-btn-yellow">Come sew with me <ArrowRight size={18} aria-hidden="true" /></a>
                <svg className="b2-youin-ring" viewBox="0 0 300 110" preserveAspectRatio="none" aria-hidden="true"><Draw d="M210 10 C 120 -4, 20 12, 8 52 C -2 92, 120 106, 220 98 C 300 90, 304 40, 260 18 C 240 8, 214 6, 190 9" delay={2} width={3} /></svg>
              </span>
              <a href="#inside" className="b2-btn b2-btn-ghost">Take a peek</a>
            </div>
            <p className="c2-price-note"><b>$25/month</b> · Live classes + recordings · Cancel anytime</p>
          </motion.div>
        </div>

        <div className="c2-hero-side">
          <motion.p className="b2-price c2-price" initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: -12 }} transition={{ delay: 1.1, type: "spring", stiffness: 220, damping: 13 }}>
            <b>$25</b>a month<small>cancel anytime</small>
          </motion.p>
          <OnAir />
          <motion.figure className="c2-cosmo" initial={{ opacity: 0, y: 30, rotate: -2 }} animate={{ opacity: 1, y: 0, rotate: -8 }} transition={{ delay: 0.9, type: "spring", stiffness: 140, damping: 15 }}>
            <i className="b2-snap-tape b2-tape-pink" aria-hidden="true" />
            <span><img src="/chelsea-and-cosmo.jpg" alt="Cosmo the cat on Chelsea’s lap at the sewing machine" /></span>
            <figcaption>Cosmo never<br />misses a class <b aria-hidden="true">♥</b></figcaption>
          </motion.figure>
          <LiveDates className="c2-dates" onMount />
        </div>
      </div>
    </div>
  </section>;
}

const MAKES = ["Quilts & patchwork", "Clothes & bags", "Gifts & creative detours"];

function ThisMonth() {
  return <section id="inside" className="b2-sec c2-month" aria-labelledby="c2-month-title">
    <div className="b2-wrap c2-month-grid">
      <div className="c2-month-photo">
        <motion.figure className="c2-polaroid" initial={{ opacity: 0, y: 40, rotate: 6 }} whileInView={{ opacity: 1, y: 0, rotate: -3 }} viewport={VIEW} transition={{ type: "spring", stiffness: 120, damping: 15 }}>
          <i className="b2-snap-tape b2-tape-yellow" aria-hidden="true" />
          <span><img src="/chelsea.JPG" alt="Chelsea wearing her handmade cereal-print outfit and a colorful quilted bag" /></span>
          <figcaption>Yep, I made the outfit <u>AND</u> the bag. <b aria-hidden="true">♥</b></figcaption>
        </motion.figure>
        <motion.p className="c2-sample" initial={{ scale: 0, rotate: -30 }} whileInView={{ scale: 1, rotate: 10 }} viewport={VIEW} transition={{ delay: 0.5, type: "spring", stiffness: 220, damping: 13 }}>sample<br />project</motion.p>
      </div>

      <div>
        <Head id="c2-month-title" hand="this month, let’s make" word="a bag" tone="blue" className="c2-head-sm" />
        <p className="b2-lede"><b>Your new favorite bag.</b> Let’s give a patchwork tote some personality! Pick your fabrics and sew along with me. I’ll walk you through the steps so you can enjoy making a bag you’ll reach for every day.</p>
        <p className="b2-note c2-rabbit">a new creative rabbit hole every month:</p>
        <ul className="c2-makes">
          {MAKES.map((m, i) => <li key={m}>{m}<svg viewBox="0 0 240 70" preserveAspectRatio="none" aria-hidden="true"><Ink d="M180 8 C 110 -2, 14 8, 8 36 C 2 64, 120 70, 200 60 C 250 52, 244 18, 200 10" delay={0.4 + i * 0.25} width={2.4} /></svg></li>)}
        </ul>
        <div className="c2-month-join">
          <a href={PATREON_URL} target="_blank" rel="noreferrer" className="b2-btn b2-btn-blue">See the current project <ArrowUpRight size={16} aria-hidden="true" /></a>
          <p className="c2-fine">Sample project shown. Check Patreon for the actual project, dates, skills, and supplies. Patterns and supplies are separate unless I say otherwise.</p>
        </div>
      </div>
    </div>
  </section>;
}

const WAYS = [
  {
    key: "live", tone: "pop", tape: "pink", tag: <><i className="c2-dot" />Sew with me live</>, title: "Your creative night in.",
    text: "Grab your fabric, settle in, and sew with me. Ask me that question. Let’s work through the tricky bit together. I’m here for the fun, the little wins, and the thread-covered chaos.",
    checks: ["Ask me questions as we sew", "Let me help with the tricky steps", "Make time for something you love"],
    foot: <>{CLUB_SCHEDULE}<small>My usual class schedule, unless I announce a change.</small></>,
  },
  {
    key: "replay", tone: "card", tape: "green", tag: <><Play size={13} fill="currentColor" aria-hidden="true" />Sew on your schedule</>, title: "Your pace. Your happy place.",
    text: "I know life gets busy. Maybe your sewing time is Sunday morning or after everyone’s in bed. I include the class recordings so you can sew along with me whenever it works for you.",
    checks: ["Watch when you have time", "Pause while you cut, pin, and sew", "Replay a step until it clicks"],
    foot: <>I’d love to have you here, even if you watch later.<small>My class recordings are included in your membership.</small></>,
  },
] as const;

function TwoWays() {
  return <section className="b2-sec c2-ways" aria-labelledby="c2-ways-title">
    <div className="b2-wrap">
      <Head id="c2-ways-title" hand="live or" word="later" tone="blue" className="b2-head-center" />
      <p className="b2-lede c2-center">I’ll guide you through one bigger project each month in two classes. Join me live or make it a cozy replay afternoon.</p>
      <div className="c2-ways-grid">
        {WAYS.map((w, i) => <motion.article key={w.key} className={`c2-way b2-sticky-${w.tone}`}
          initial={{ opacity: 0, y: 40, rotate: [-2, 2][i] + 5 }} whileInView={{ opacity: 1, y: 0, rotate: [-2, 1.5][i] }} viewport={VIEW} transition={{ delay: i * 0.12, type: "spring", stiffness: 130, damping: 15 }}>
          <i className={`b2-snap-tape b2-tape-${w.tape}`} aria-hidden="true" />
          <p className={`c2-way-tag c2-way-tag-${w.key}`}>{w.tag}</p>
          <h3>{w.title}</h3>
          <p>{w.text}</p>
          <Checks items={[...w.checks]} className="c2-way-checks" />
          <p className="c2-way-foot">{w.foot}</p>
        </motion.article>)}
        <p className="b2-note c2-pj" aria-hidden="true">pajamas<br />encouraged</p>
      </div>
    </div>
  </section>;
}

const PILE = [
  { src: "/concept/gingham-top.jpg", alt: "A blue gingham top with puff sleeves", caption: "share your makes", r: -6 },
  { src: "/img/products/qs-mat.jpg", alt: "A tall stack of colorful folded fabric", caption: "join a challenge", r: 3, zoom: true },
  { src: "/chelsea-rainbow-portrait.jpg", alt: "Chelsea smiling in her straw hat", caption: "celebrate the little wins", r: -2 },
];

function Company() {
  return <section className="b2-sec c2-company" aria-labelledby="c2-company-title">
    <div className="b2-wrap c2-company-grid">
      <div>
        <p className="b2-kicker">Your project pile is in good company</p>
        <Head id="c2-company-title" hand="people who" word="get it" tone="pink" />
        <p className="b2-lede">I built this club for people who love making things. Share your makes, join a challenge, and celebrate the little wins with me and other sewists who get why you needed just one more piece of fabric.</p>
        <blockquote className="c2-quote">
          <p>“It’ll be fun, slightly unhinged, and very thread-covered.”</p>
          <footer>xo, Chelsea <svg viewBox="0 0 30 46" aria-hidden="true"><Ink d={HEART} delay={0.8} width={3.4} /></svg></footer>
        </blockquote>
      </div>
      <ul className="c2-pile">
        {PILE.map((p, i) => <motion.li key={p.src} initial={{ opacity: 0, y: 50, rotate: p.r + 10 }} whileInView={{ opacity: 1, y: 0, rotate: p.r }} whileHover={{ y: -8, rotate: 0, zIndex: 5 }} viewport={VIEW}
          transition={{ delay: i * 0.12, type: "spring", stiffness: 130, damping: 15 }}>
          <i className={`b2-snap-tape b2-tape-${["yellow", "pink", "green"][i]}`} aria-hidden="true" />
          <span className={p.zoom ? "is-zoom" : undefined}><img src={p.src} alt={p.alt} /></span>
          <b>{p.caption}</b>
        </motion.li>)}
      </ul>
    </div>
  </section>;
}

function Membership() {
  return <section id="join" className="b2-sec c2-join" aria-labelledby="c2-join-title">
    <div className="b2-wrap c2-join-grid">
      <div>
        <Head id="c2-join-title" hand="I’ve saved you a" word="chair" tone="blue" />
        <p className="b2-lede">I’ll bring the project and a little help along the way. You bring your fabric, your questions, and a little time for yourself. Let’s make something we’re excited about.</p>
        <p className="b2-note c2-chaos">Come as you are.<br />Bring your creative chaos.</p>
      </div>

      <div className="c2-pass-wrap">
        <motion.article className="c2-pass" aria-label="Loose Threads Club membership, $25 a month" initial={{ opacity: 0, y: 40, rotate: 3 }} whileInView={{ opacity: 1, y: 0, rotate: -1.5 }} viewport={VIEW} transition={{ type: "spring", stiffness: 110, damping: 16 }}>
          <div className="c2-pass-body">
            <p className="c2-pass-kicker">Admit one · every month</p>
            <p className="c2-pass-name">Loose Threads Club</p>
            <p className="c2-pass-price"><b>$25</b>/ month</p>
            <p className="c2-pass-intro">Sew with me live or catch up on your own time.</p>
            <Checks items={CLUB_INCLUDES} className="c2-pass-checks" />
          </div>
          <a className="c2-pass-stub" href={JOIN_URL}>
            <small>Join on Patreon</small>
            <b>Come <br />sew!</b>
            <ArrowRight size={26} aria-hidden="true" />
          </a>
        </motion.article>
        <p className="c2-fine c2-pass-fine">Billed monthly. Cancel anytime through Patreon. Patterns and supplies are separate unless stated.</p>
        <a href={THREAD_FRIEND_URL} className="b2-spot b2-spot-pink c2-friend">
          <small>Just want to cheer me on?</small>
          <b>Thread Friend · $3/month</b>
          <span>Help me keep creating. You’ll get merch discounts and occasional codes. This tier doesn’t include classes or replays.</span>
          <em>Become a Thread Friend <ArrowRight size={15} aria-hidden="true" /></em>
        </a>
      </div>
    </div>
  </section>;
}

function Faq() {
  return <section className="b2-sec c2-faq" aria-labelledby="c2-faq-title">
    <div className="b2-wrap c2-faq-grid">
      <div className="c2-faq-head">
        <Head id="c2-faq-title" hand="a few things you might" word="wonder" tone="pink" className="c2-head-sm" />
        <p className="b2-note">still getting to know me?</p>
        <a href={SOCIALS.youtube.url} target="_blank" rel="noreferrer" className="b2-btn b2-btn-blue">Watch my YouTube videos <ArrowUpRight size={16} aria-hidden="true" /></a>
      </div>
      <div className="c2-faq-list">
        {CLUB_FAQS.map((f, i) => <motion.details key={f.question} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: i * 0.06, duration: 0.5, ease: EASE }}>
          <summary>{f.question}<span aria-hidden="true" /></summary>
          <p>{f.answer}</p>
        </motion.details>)}
      </div>
    </div>
  </section>;
}

function YouIn() {
  return <section className="b2-sec c2-youin" aria-labelledby="c2-youin-title">
    <div className="b2-wrap">
      <Head id="c2-youin-title" hand="so…" word="you in?" tone="pink" className="b2-head-center" />
      <div className="c2-youin-join">
        <span className="b2-youin-wrap">
          <a href={JOIN_URL} className="b2-btn b2-btn-yellow b2-btn-big">Join the club <ArrowRight size={18} aria-hidden="true" /></a>
          <svg className="b2-youin-ring" viewBox="0 0 300 110" preserveAspectRatio="none" aria-hidden="true"><Ink d="M210 10 C 120 -4, 20 12, 8 52 C -2 92, 120 106, 220 98 C 300 90, 304 40, 260 18 C 240 8, 214 6, 190 9" delay={0.6} width={3} /></svg>
        </span>
        <p>$25/month · two live classes + every replay · cancel anytime</p>
      </div>
      <svg className="c2-youin-heart" viewBox="0 0 30 46" aria-hidden="true"><Ink d={HEART} delay={1.1} width={3.4} /></svg>
    </div>
  </section>;
}

export function ClubBlue2() {
  useFonts();
  return <div className="h5 b2 c2" style={rootStyle}>
    <EventStrip />
    <Part p="hero"><Hero /></Part>
    <Part p="making"><ThisMonth /></Part>
    <Part p="words"><TwoWays /></Part>
    <Part p="club"><Company /></Part>
    <Part p="loop"><Membership /></Part>
    <Part p="faves"><Faq /></Part>
    <Part p="where"><YouIn /></Part>
    <Part p="footer"><Footer /></Part>
  </div>;
}
